import CryptoJS from 'crypto-js';

/**
 * Centralized Storage Helper Utility with Automatic Encryption
 * 
 * This utility provides a consistent interface for storage operations
 * using sessionStorage with automatic AES-256 encryption for all data.
 * Data is transparently encrypted when stored and decrypted when retrieved.
 * 
 * Features:
 * - Automatic encryption/decryption for all stored data
 * - AES-256 encryption with random IV for each operation
 * - Backward compatible API (no code changes needed)
 * - Error handling with fallback values
 * - Session-based storage that clears on browser close
 */

class StorageHelper {
    static #encryptionKey = null;
    static #initialized = false;

    /**
     * Initialize the encryption system
     * @private
     */
    static #initializeEncryption() {
        if (this.#initialized) return;

        try {
            const envKey = process.env.REACT_APP_ENCRYPTION_KEY;
            if (!envKey) {
                console.warn('StorageHelper: REACT_APP_ENCRYPTION_KEY not found. Using fallback key.');
                // Fallback key for development - should not be used in production
                this.#encryptionKey = CryptoJS.PBKDF2('doves-fallback-key-2025', 'doves-salt', {
                    keySize: 256/32,
                    iterations: 1000
                });
            } else {
                // Generate a consistent key using PBKDF2 for better security
                this.#encryptionKey = CryptoJS.PBKDF2(envKey, 'doves-storage-salt-2025', {
                    keySize: 256/32,
                    iterations: 1000
                });
            }
            this.#initialized = true;
        } catch (error) {
            console.error('StorageHelper: Failed to initialize encryption:', error);
            throw new Error('Storage encryption initialization failed');
        }
    }

    /**
     * Encrypt data for storage
     * @private
     * @param {any} data - Data to encrypt
     * @returns {string|null} - Encrypted string or null if encryption fails
     */
    static #encrypt(data) {
        try {
            this.#initializeEncryption();

            if (data === undefined || data === null) {
                return null;
            }

            // Convert data to JSON string
            const jsonString = JSON.stringify(data);
            
            // Generate random IV for each encryption
            const iv = CryptoJS.lib.WordArray.random(128/8);
            
            // Encrypt the data
            const encrypted = CryptoJS.AES.encrypt(jsonString, this.#encryptionKey, {
                iv: iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            });
            
            // Combine IV and encrypted data
            const combined = iv.concat(encrypted.ciphertext);
            
            // Return as base64 string with prefix to identify encrypted data
            return 'ENC:' + combined.toString(CryptoJS.enc.Base64);
            
        } catch (error) {
            console.error('StorageHelper: Encryption error:', error);
            return null;
        }
    }

    /**
     * Decrypt data from storage
     * @private
     * @param {string} encryptedData - Encrypted string to decrypt
     * @returns {any|null} - Decrypted data or null if decryption fails
     */
    static #decrypt(encryptedData) {
        try {
            this.#initializeEncryption();

            if (!encryptedData || typeof encryptedData !== 'string') {
                return null;
            }

            // Check if data is encrypted (has ENC: prefix)
            if (!encryptedData.startsWith('ENC:')) {
                // Legacy unencrypted data - return as is but try to parse JSON
                try {
                    return JSON.parse(encryptedData);
                } catch {
                    return encryptedData;
                }
            }

            // Remove the ENC: prefix
            const base64Data = encryptedData.substring(4);

            // Convert from base64
            const combined = CryptoJS.enc.Base64.parse(base64Data);
            
            // Extract IV (first 16 bytes) and ciphertext
            const iv = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4));
            const ciphertext = CryptoJS.lib.WordArray.create(combined.words.slice(4));
            
            // Create cipher params
            const cipherParams = CryptoJS.lib.CipherParams.create({
                ciphertext: ciphertext
            });
            
            // Decrypt the data
            const decrypted = CryptoJS.AES.decrypt(cipherParams, this.#encryptionKey, {
                iv: iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            });
            
            // Convert to string
            const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
            
            if (!decryptedString) {
                console.warn('StorageHelper: Failed to decrypt data');
                return null;
            }
            
            // Parse JSON back to original object
            return JSON.parse(decryptedString);
            
        } catch (error) {
            console.error('StorageHelper: Decryption error:', error);
            return null;
        }
    }
    /**
     * Get data from sessionStorage (automatically decrypted)
     * @param {string} key - The key to retrieve
     * @param {any} defaultValue - Default value if key doesn't exist or decryption fails
     * @returns {any} - The stored value or default value
     */
    static get(key, defaultValue = null) {
        try {
            const item = sessionStorage.getItem(key);
            if (item === null) {
                return defaultValue;
            }
            
            // Decrypt the data
            const decrypted = this.#decrypt(item);
            if (decrypted === null) {
                return defaultValue;
            }
            
            return decrypted;
        } catch (error) {
            console.error(`StorageHelper: Error getting item with key "${key}":`, error);
            return defaultValue;
        }
    }

    /**
     * Set data in sessionStorage (automatically encrypted)
     * @param {string} key - The key to store
     * @param {any} value - The value to store
     * @returns {boolean} - Success status
     */
    static set(key, value) {
        try {
            if (value === undefined || value === null) {
                sessionStorage.removeItem(key);
                return true;
            }

            // Encrypt the data
            const encrypted = this.#encrypt(value);
            if (encrypted === null) {
                console.error(`StorageHelper: Failed to encrypt data for key "${key}"`);
                return false;
            }

            sessionStorage.setItem(key, encrypted);
            return true;
        } catch (error) {
            console.error(`StorageHelper: Error setting item with key "${key}":`, error);
            return false;
        }
    }

    /**
     * Remove data from sessionStorage
     * @param {string} key - The key to remove
     * @returns {boolean} - Success status
     */
    static remove(key) {
        try {
            sessionStorage.removeItem(key);
            return true;
        } catch (error) {
            console.error(`StorageHelper: Error removing item with key "${key}":`, error);
            return false;
        }
    }

    /**
     * Clear all data from sessionStorage
     * @returns {boolean} - Success status
     */
    static clear() {
        try {
            sessionStorage.clear();
            return true;
        } catch (error) {
            console.error('StorageHelper: Error clearing sessionStorage:', error);
            return false;
        }
    }

    /**
     * Check if a key exists in sessionStorage
     * @param {string} key - The key to check
     * @returns {boolean} - Whether the key exists
     */
    static has(key) {
        try {
            return sessionStorage.getItem(key) !== null;
        } catch (error) {
            console.error(`StorageHelper: Error checking if key "${key}" exists:`, error);
            return false;
        }
    }

    /**
     * Get all keys from sessionStorage
     * @returns {string[]} - Array of all keys
     */
    static getAllKeys() {
        try {
            const keys = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                keys.push(sessionStorage.key(i));
            }
            return keys;
        } catch (error) {
            console.error('StorageHelper: Error getting all keys from sessionStorage:', error);
            return [];
        }
    }

    /**
     * Get the storage size (approximate)
     * @returns {number} - Storage size in bytes
     */
    static getStorageSize() {
        try {
            let total = 0;
            for (let key in sessionStorage) {
                if (sessionStorage.hasOwnProperty(key)) {
                    total += sessionStorage[key].length + key.length;
                }
            }
            return total;
        } catch (error) {
            console.error('StorageHelper: Error calculating sessionStorage size:', error);
            return 0;
        }
    }

    /**
     * Validate that the encryption system is working properly
     * @returns {boolean} - True if encryption/decryption works correctly
     */
    static validateEncryption() {
        try {
            const testData = {
                string: 'test',
                number: 12345,
                boolean: true,
                object: { nested: 'value' },
                array: [1, 2, 3]
            };
            
            const testKey = 'encryption_test_key';
            
            // Test set and get
            const setSuccess = this.set(testKey, testData);
            if (!setSuccess) return false;
            
            const retrieved = this.get(testKey);
            const isEqual = JSON.stringify(testData) === JSON.stringify(retrieved);
            
            // Clean up test data
            this.remove(testKey);
            
            return isEqual;
        } catch (error) {
            console.error('StorageHelper: Encryption validation error:', error);
            return false;
        }
    }

    /**
     * Get information about the storage encryption status
     * @returns {object} - Encryption status and statistics
     */
    static getEncryptionInfo() {
        try {
            const keys = this.getAllKeys();
            let encryptedCount = 0;
            let unencryptedCount = 0;
            
            for (const key of keys) {
                const rawValue = sessionStorage.getItem(key);
                if (rawValue && rawValue.startsWith('ENC:')) {
                    encryptedCount++;
                } else {
                    unencryptedCount++;
                }
            }
            
            return {
                totalKeys: keys.length,
                encryptedKeys: encryptedCount,
                unencryptedKeys: unencryptedCount,
                encryptionEnabled: this.#initialized,
                encryptionWorking: this.validateEncryption()
            };
        } catch (error) {
            console.error('StorageHelper: Error getting encryption info:', error);
            return {
                totalKeys: 0,
                encryptedKeys: 0,
                unencryptedKeys: 0,
                encryptionEnabled: false,
                encryptionWorking: false
            };
        }
    }

    /**
     * Migrate existing unencrypted data to encrypted format
     * @param {string[]} keysToMigrate - Array of keys to migrate (optional, migrates all if not provided)
     * @returns {object} - Migration results
     */
    static migrateToEncrypted(keysToMigrate = null) {
        try {
            const keys = keysToMigrate || this.getAllKeys();
            let migrated = 0;
            let failed = 0;
            let alreadyEncrypted = 0;
            
            for (const key of keys) {
                const rawValue = sessionStorage.getItem(key);
                if (!rawValue) continue;
                
                // Skip if already encrypted
                if (rawValue.startsWith('ENC:')) {
                    alreadyEncrypted++;
                    continue;
                }
                
                try {
                    // Get the unencrypted value
                    const unencryptedValue = this.#decrypt(rawValue);
                    if (unencryptedValue !== null) {
                        // Re-store it (this will encrypt it)
                        const success = this.set(key, unencryptedValue);
                        if (success) {
                            migrated++;
                        } else {
                            failed++;
                        }
                    } else {
                        failed++;
                    }
                } catch (error) {
                    console.error(`StorageHelper: Failed to migrate key "${key}":`, error);
                    failed++;
                }
            }
            
            return {
                totalProcessed: keys.length,
                migrated,
                failed,
                alreadyEncrypted
            };
        } catch (error) {
            console.error('StorageHelper: Migration error:', error);
            return {
                totalProcessed: 0,
                migrated: 0,
                failed: 0,
                alreadyEncrypted: 0
            };
        }
    }
}

// Named exports for convenience
export const getStorageItem = StorageHelper.get;
export const setStorageItem = StorageHelper.set;
export const removeStorageItem = StorageHelper.remove;
export const clearStorage = StorageHelper.clear;
export const hasStorageItem = StorageHelper.has;
export const getAllStorageKeys = StorageHelper.getAllKeys;
export const getStorageSize = StorageHelper.getStorageSize;

// Default export
export default StorageHelper;